/*
 * Spinlab metinleri — beş dil: tr, en, ru, fr, de (09.10.2026).
 *
 * Kaynak tema metni iki yoldan çeker (kataloglar scripts/spinlab-i18n.js ile
 * üretilir, eksik çeviri betikte hatadır):
 *   * `tk('vip.title')` — kaynak kataloğun anahtarı (+ sitenin `spl.*`
 *     anahtarları),
 *   * `t('Sign In')`    — kaynağın İngilizce arayüz metni.
 * Kabuk HTML'i Türkçe üretilmiştir; metin düğümleri `shellText()` ile seçili
 * dile çevrilir.
 *
 * Dil seçimi tarayıcıda saklanır (`spl:lang`), `<html lang>`'ı günceller ve
 * sayfayı yenilemeden yeniden çizer (`useLanguage`). Adresler dile göre
 * değişmez. Varsayılan Türkçe'dir.
 */
import { useEffect, useState } from 'react';
import { KEYS, SHELL, TEXTS } from './generated/locales';

export const LANGUAGES = [
  { code: 'tr', name: 'Türkçe', locale: 'tr-TR' },
  { code: 'en', name: 'English', locale: 'en-US' },
  { code: 'ru', name: 'Русский', locale: 'ru-RU' },
  { code: 'fr', name: 'Français', locale: 'fr-FR' },
  { code: 'de', name: 'Deutsch', locale: 'de-DE' },
];
const CODES = LANGUAGES.map((l) => l.code);
export const LANGUAGE_STORAGE_KEY = 'spl:lang';
const DEFAULT = 'tr';

const readStored = () => {
  try {
    const value = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return CODES.includes(value) ? value : DEFAULT;
  } catch {
    return DEFAULT;
  }
};

let current = typeof window === 'undefined' ? DEFAULT : readStored();
const listeners = new Set();

export const getLanguage = () => current;
export const languageInfo = (code = current) => LANGUAGES.find((l) => l.code === code) || LANGUAGES[0];

const applyDocument = () => {
  if (typeof document !== 'undefined') document.documentElement.setAttribute('lang', current);
};

export const setLanguage = (code) => {
  if (!CODES.includes(code) || code === current) return;
  current = code;
  try { window.localStorage.setItem(LANGUAGE_STORAGE_KEY, code); } catch { /* gizli sekme: oturum boyunca geçerli */ }
  applyDocument();
  listeners.forEach((fn) => fn(code));
};

export const onLanguageChange = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

/** Bileşeni dil değişiminde yeniden çizer; geçerli dili döndürür. */
export const useLanguage = () => {
  const [lang, setLang] = useState(current);
  useEffect(() => {
    applyDocument();
    return onLanguageChange(setLang);
  }, []);
  return lang;
};

const has = (obj, key) => Boolean(obj) && Object.prototype.hasOwnProperty.call(obj, key);
const fill = (text, vars = {}) => Object.entries(vars).reduce(
  (out, [name, value]) => out.split(`{${name}}`).join(String(value)), text,
);

/** Kaynağın İngilizce arayüz metni → seçili dil (yoksa İngilizce). */
export const t = (text) => {
  if (has(TEXTS[current], text)) return TEXTS[current][text];
  if (has(TEXTS.en, text)) return TEXTS.en[text];
  return text;
};

/** Kaynak kataloğun anahtarıyla çeviri (`tk('challenges.playNow')`), kalıp değişkenleriyle. */
export const tk = (key, vars = {}) => {
  const value = has(KEYS[current], key) ? KEYS[current][key] : (has(KEYS.en, key) ? KEYS.en[key] : key);
  return fill(value, vars);
};

/** Kalıplı metin: `tf('{count} games found', { count: 3 })`. */
export const tf = (text, vars = {}) => fill(t(text), vars);

/** Kabuğun Türkçe metni → seçili dil. */
export const shellText = (turkish) => {
  if (current === 'tr' || typeof turkish !== 'string') return turkish;
  const trimmed = turkish.trim();
  if (!has(SHELL, trimmed)) return turkish;
  return turkish.replace(trimmed, t(SHELL[trimmed]));
};

const locale = () => languageInfo().locale;

/**
 * Tutar biçimi. Kaynak tema para birimi simgesini tutarın **önüne** yazar
 * (`$0.00`); site TRY ile çalışır, simge aynı yerde kalır. Ondalık ayracı
 * seçili dilin biçimidir.
 */
export const money = (value) => `₺${new Intl.NumberFormat(locale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value) || 0)}`;

/** Sunucunun UTC zaman damgasını yerel saate çevirir. */
export const dateTime = (value) => {
  if (!value) return '—';
  const text = String(value);
  const date = new Date(/[zZ]|[+-]\d\d:\d\d$/.test(text) ? text : `${text}Z`);
  return Number.isNaN(date.getTime()) ? '—' : new Intl.DateTimeFormat(locale(), {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(date);
};
